import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Heart, Save, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';
import FocusPromptList from '../../components/FocusPromptList';

const ROUND_TYPES = [
  { key: 'practice', label: 'Practice' },
  { key: 'casual', label: 'Casual' },
  { key: 'competitive', label: 'Competitive' },
  { key: 'tournament', label: 'Tournament' },
];

const STEPS = [
  { key: 'context', label: 'Round', short: '1' },
  { key: 'awareness', label: 'Awareness', short: 'A1', icon: Brain, color: '#2C5530' },
  { key: 'acceptance', label: 'Acceptance', short: 'A2', icon: Heart, color: '#BC562B' },
  { key: 'action', label: 'Action', short: 'A3', icon: ArrowRight, color: '#1A2E1D' },
  { key: 'review', label: 'Review', short: '5' },
];

const KpiSlider = ({ label, value, onChange, color }) => (
  <div className="py-3">
    <div className="flex items-baseline justify-between mb-1.5">
      <span className="text-sm font-semibold text-[#1A1A1A]">{label}</span>
      <span className="font-['Playfair_Display'] text-xl tabular-nums" style={{ color }}>
        {value}<span className="text-xs text-[#737373]"> / 10</span>
      </span>
    </div>
    <input
      type="range"
      min={1}
      max={10}
      step={1}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="w-full accent-[#2C5530]"
    />
  </div>
);

const RoundReviewWizardPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Context
  const [roundDate, setRoundDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [courseName, setCourseName] = useState('');
  const [roundType, setRoundType] = useState('');
  const [score, setScore] = useState('');
  const [scoreToPar, setScoreToPar] = useState('');

  // Quick mode scores for each KPI
  const [aware, setAware] = useState(7);
  const [accept, setAccept] = useState(7);
  const [action, setAction] = useState(7);

  // Notes per KPI
  const [awareNote, setAwareNote] = useState('');
  const [acceptNote, setAcceptNote] = useState('');
  const [actionNote, setActionNote] = useState('');

  // Overall reflection
  const [overallReflection, setOverallReflection] = useState('');

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const gapAa = aware - action;
  const gapBlend = +(aware - (accept + action) / 2.0).toFixed(2);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        round_date: roundDate,
        course_name: courseName.trim() || null,
        round_type: roundType || null,
        score: score === '' ? null : parseInt(score, 10),
        score_to_par: scoreToPar === '' ? null : parseInt(scoreToPar, 10),
        overall_reflection: overallReflection.trim() || null,
        awareness: { mode: 'quick', overall: aware, reflection: awareNote.trim() || null },
        acceptance: { mode: 'quick', overall: accept, reflection: acceptNote.trim() || null },
        action: { mode: 'quick', overall: action, reflection: actionNote.trim() || null },
      };
      const { data } = await API.post('/locker/rounds', payload);
      toast.success('Round review saved.');
      navigate(`/locker/rounds/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save round.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="round-wizard-page">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-3xl flex items-center gap-3">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-3xl">
        {/* Stepper */}
        <ol className="flex items-center justify-between mb-8 gap-1" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <li key={s.key} className="flex-1 flex items-center">
              <button
                type="button"
                onClick={() => i <= step && setStep(i)}
                className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
                  i === step ? 'text-[#1A1A1A]' : i < step ? 'text-[#2C5530]' : 'text-[#A8A29E]'
                }`}
                data-testid={`step-${s.key}`}
              >
                <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-[10px] ${
                  i === step ? 'bg-[#1A2E1D] text-white' : i < step ? 'bg-[#2C5530] text-white' : 'bg-[#E7E5E4] text-[#737373]'
                }`}>
                  {i < step ? <CheckCircle2 className="w-3 h-3" /> : s.short}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="flex-1 h-px bg-[#E7E5E4] mx-1" />}
            </li>
          ))}
        </ol>

        {/* Step 0: Context */}
        {step === 0 && (
          <div className="bg-white border border-[#E7E5E4] p-6 space-y-5" data-testid="wizard-context">
            <div>
              <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Round details</p>
              <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Set the scene</h1>
              <p className="text-[#57534E] text-sm">A few facts so future-you can find this round.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> Round date
                </label>
                <input type="date" value={roundDate} onChange={(e) => setRoundDate(e.target.value)} className="w-full" data-testid="wizard-date" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Course (optional)</label>
                <input type="text" value={courseName} onChange={(e) => setCourseName(e.target.value)} placeholder="Bristol Golf Club" className="w-full" data-testid="wizard-course" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Round type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ROUND_TYPES.map((rt) => (
                  <button
                    type="button"
                    key={rt.key}
                    onClick={() => setRoundType(roundType === rt.key ? '' : rt.key)}
                    className={`text-xs font-semibold uppercase tracking-wider px-3 py-2.5 border transition-colors ${
                      roundType === rt.key ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]' : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
                    }`}
                    data-testid={`wizard-round-type-${rt.key}`}
                  >
                    {rt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Total strokes (optional)</label>
                <input type="number" value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 82" className="w-full max-w-[200px]" data-testid="wizard-score" />
                <p className="text-xs text-[#A8A29E] mt-1">Leave blank to keep it mental-only.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Score to par (optional)</label>
                <input type="number" value={scoreToPar} onChange={(e) => setScoreToPar(e.target.value)} placeholder="e.g. 10 or -2" className="w-full max-w-[200px]" data-testid="wizard-score-to-par" />
                <p className="text-xs text-[#A8A29E] mt-1">Feeds the Score vs Mental chart on your Locker.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Awareness */}
        {step === 1 && (
          <div className="bg-white border border-[#E7E5E4] p-6" data-testid="wizard-awareness">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-[#2C5530] text-white flex items-center justify-center"><Brain className="w-5 h-5" /></span>
              <div>
                <p className="text-[#2C5530] text-xs uppercase tracking-[0.25em]">Awareness</p>
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">How aware were you?</h2>
              </div>
            </div>
            <p className="text-sm text-[#57534E] mb-4">Emotions noticed, focus felt, body tension caught — honest beats high.</p>
            <KpiSlider label="Overall Awareness" value={aware} onChange={setAware} color="#2C5530" />
            <div className="mt-4">
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Awareness note (optional)</label>
              <textarea value={awareNote} onChange={(e) => setAwareNote(e.target.value)} rows={3} className="w-full" placeholder="What did you notice?" data-testid="wizard-aware-note" />
            </div>
          </div>
        )}

        {/* Step 2: Acceptance */}
        {step === 2 && (
          <div className="bg-white border border-[#E7E5E4] p-6" data-testid="wizard-acceptance">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-[#BC562B] text-white flex items-center justify-center"><Heart className="w-5 h-5" /></span>
              <div>
                <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em]">Acceptance</p>
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">How accepting were you?</h2>
              </div>
            </div>
            <p className="text-sm text-[#57534E] mb-4">Outcomes, self-talk, patience, letting go — how light did you travel?</p>
            <KpiSlider label="Overall Acceptance" value={accept} onChange={setAccept} color="#BC562B" />
            <div className="mt-4">
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Acceptance note (optional)</label>
              <textarea value={acceptNote} onChange={(e) => setAcceptNote(e.target.value)} rows={3} className="w-full" placeholder="Where did you resist? Where did you accept?" data-testid="wizard-accept-note" />
            </div>
          </div>
        )}

        {/* Step 3: Action */}
        {step === 3 && (
          <div className="bg-white border border-[#E7E5E4] p-6" data-testid="wizard-action">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-[#1A2E1D] text-white flex items-center justify-center"><ArrowRight className="w-5 h-5" /></span>
              <div>
                <p className="text-[#1A2E1D] text-xs uppercase tracking-[0.25em]">Action</p>
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">How well did you commit?</h2>
              </div>
            </div>
            <p className="text-sm text-[#57534E] mb-4">Routine, target, execution, reset — did awareness become action?</p>
            <KpiSlider label="Overall Action" value={action} onChange={setAction} color="#1A2E1D" />
            <div className="mt-4">
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Action note (optional)</label>
              <textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} rows={3} className="w-full" placeholder="When did you fully commit? When did you hesitate?" data-testid="wizard-action-note" />
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="bg-white border border-[#E7E5E4] p-6 space-y-5" data-testid="wizard-review">
            <div>
              <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Review</p>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Your round, distilled</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-[#F2F0E9] p-4">
                <p className="text-[10px] uppercase tracking-wider text-[#737373]">Awareness</p>
                <p className="font-['Playfair_Display'] text-3xl text-[#2C5530] tabular-nums">{aware}<span className="text-sm text-[#737373]">/10</span></p>
              </div>
              <div className="bg-[#F2F0E9] p-4">
                <p className="text-[10px] uppercase tracking-wider text-[#737373]">Acceptance</p>
                <p className="font-['Playfair_Display'] text-3xl text-[#BC562B] tabular-nums">{accept}<span className="text-sm text-[#737373]">/10</span></p>
              </div>
              <div className="bg-[#F2F0E9] p-4">
                <p className="text-[10px] uppercase tracking-wider text-[#737373]">Action</p>
                <p className="font-['Playfair_Display'] text-3xl text-[#1A2E1D] tabular-nums">{action}<span className="text-sm text-[#737373]">/10</span></p>
              </div>
            </div>
            <div className="bg-[#1A2E1D] text-white p-4 grid grid-cols-2 gap-4" data-testid="wizard-gap-preview">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/60">The Gap (A1 − A3)</p>
                <p className="font-['Playfair_Display'] text-2xl tabular-nums">{gapAa > 0 ? `+${gapAa}` : gapAa}</p>
                <p className="text-xs text-white/70">{gapAa > 1 ? 'Aware but not yet acting' : gapAa < -1 ? 'Acting without much awareness' : 'Tightly aligned'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/60">Blend gap</p>
                <p className="font-['Playfair_Display'] text-2xl tabular-nums">{gapBlend > 0 ? `+${gapBlend}` : gapBlend}</p>
                <p className="text-xs text-white/70">Awareness vs. avg(Acceptance, Action)</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Overall reflection (optional)</label>
              <textarea
                value={overallReflection}
                onChange={(e) => setOverallReflection(e.target.value)}
                rows={4}
                placeholder="What was the headline of this round?"
                className="w-full"
                data-testid="wizard-overall-reflection"
              />
            </div>

            {/* Daily focus prompts — tick them off after the round */}
            <FocusPromptList
              mode="player"
              title="Tick today's focus"
              subtitle="Mark off the focus prompts you stuck with."
            />
          </div>
        )}

        {/* Nav */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
          {step > 0 ? (
            <button onClick={back} className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9]" data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <Link to="/locker" className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9]">
              Cancel
            </Link>
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21]" data-testid="wizard-next">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] disabled:opacity-50" data-testid="wizard-submit">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save round review'}
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default RoundReviewWizardPage;
